
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/sections/Footer";
import { Helmet } from "react-helmet";
import { FaqSection } from "@/components/services/cybersecurity/FaqSection";
import { HeroSection } from "@/components/services/cybersecurity/HeroSection";
import { ChallengesSection } from "@/components/services/cybersecurity/ChallengesSection";
import { ApproachSection } from "@/components/services/cybersecurity/ApproachSection";
import { ComparisonSection } from "@/components/services/cybersecurity/ComparisonSection";
import { ProcessSection } from "@/components/services/cybersecurity/ProcessSection";
import { ResultsSection } from "@/components/services/cybersecurity/ResultsSection";
import { SuccessStoriesSection } from "@/components/services/cybersecurity/SuccessStoriesSection";
import { CallToActionSection } from "@/components/services/advisory/CallToActionSection";
import { ValueMetricsSection } from "@/components/services/specializations/ValueMetricsSection";
import { useEffect } from "react";
import { CtaSection } from "@/components/ui/cta-section";

const ManagedCybersecurity = () => {
  const pageTitle = "Managed Cybersecurity Services | 24/7 Security Protection | edgefi";
  const pageDescription = "Comprehensive managed cybersecurity services that protect your digital assets with 24/7 monitoring, threat detection, and rapid incident response.";
  const pageUrl = window.location.origin + "/services/managed-cybersecurity";

  useEffect(() => {
    const serviceScript = document.createElement('script');
    serviceScript.type = 'application/ld+json';
    serviceScript.innerHTML = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Managed Cybersecurity Services",
      "provider": {
        "@type": "Organization",
        "name": "edgefi",
        "url": window.location.origin
      },
      "serviceType": "Cybersecurity Management",
      "description": "Comprehensive cybersecurity services to protect your digital assets, detect threats, and ensure business continuity.",
      "offers": {
        "@type": "Offer",
        "description": "24/7 monitoring, threat detection, incident response, and security advisory services"
      },
      "areaServed": {
        "@type": "Country",
        "name": "United States"
      },
      "audience": {
        "@type": "BusinessAudience",
        "audienceType": "SMB, Enterprise"
      },
      "url": pageUrl
    });
    document.head.appendChild(serviceScript);
    
    const breadcrumbScript = document.createElement('script');
    breadcrumbScript.type = 'application/ld+json';
    breadcrumbScript.innerHTML = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": window.location.origin
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Services",
          "item": `${window.location.origin}/services`
        },
        {
          "@type": "ListItem",
          "position": 3,
          "name": "Managed Cybersecurity Services",
          "item": pageUrl
        }
      ]
    });
    document.head.appendChild(breadcrumbScript);
    
    const faqScript = document.createElement('script');
    faqScript.type = 'application/ld+json';
    faqScript.innerHTML = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "What is included in managed cybersecurity services?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Our managed cybersecurity services include 24/7 security monitoring, threat detection and response, vulnerability management, security assessments, incident response planning, and regular security reporting."
          }
        },
        {
          "@type": "Question",
          "name": "How quickly can you respond to security incidents?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Our security operations center provides 24/7 monitoring with initial response times of 15 minutes or less for critical alerts, and detailed incident response within hours, not days."
          }
        },
        {
          "@type": "Question",
          "name": "Do you offer compliance-specific security services?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes, we offer specialized security services tailored to various compliance requirements including HIPAA, PCI DSS, GDPR, CCPA, CMMC, and industry-specific regulations."
          }
        }
      ]
    });
    document.head.appendChild(faqScript);

    return () => {
      document.head.removeChild(serviceScript);
      document.head.removeChild(breadcrumbScript);
      document.head.removeChild(faqScript);
    };
  }, [pageUrl]);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="keywords" content="managed cybersecurity, security monitoring, threat detection, incident response, SOC, SIEM, vulnerability management" />
        <link rel="canonical" href={pageUrl} />
        
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={`${window.location.origin}/og-image.png`} />
        <meta property="og:image:alt" content="edgefi managed cybersecurity services" />
        <meta property="og:site_name" content="edgefi" />
        <meta property="og:locale" content="en_US" />
        
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={`${window.location.origin}/og-image.png`} />
        <meta name="twitter:image:alt" content="edgefi managed cybersecurity services" />
        
        <meta name="author" content="edgefi" />
        <meta name="robots" content="index, follow" />
      </Helmet>
      
      <Navbar />
      
      <main className="relative">
        <HeroSection />

        <ChallengesSection />

        <ApproachSection />

        <ComparisonSection />

        <ValueMetricsSection 
          title="Real Business Impact"
          subtitle="Measurable Security Outcomes"
          description="Our managed security services deliver tangible results that protect your business and create measurable value."
          metrics={[
            {
              value: "15 min",
              label: "Critical Alert Response",
              description: "Average response time to critical security alerts, ensuring rapid containment of threats."
            },
            {
              value: "90%",
              label: "Threat Reduction",
              description: "Average reduction in successful security incidents after implementing our managed security services."
            },
            {
              value: "24/7",
              label: "Continuous Monitoring",
              description: "Round-the-clock security monitoring and threat detection by our expert security team."
            },
            {
              value: "60%",
              label: "Cost Reduction",
              description: "Average cost savings compared to building and staffing an in-house security operations center."
            }
          ]}
        />

        <ProcessSection />

        <ResultsSection />

        <SuccessStoriesSection />
        
        {/* Replace CallToActionSection with CtaSection for more control */}
        <CtaSection
          title="Ready to contain the security chaos?"
          description="Get 24/7 managed security protection that evolves with your business."
          buttonText="Begin Your Managed Security Journey"
          buttonVariant="cta"
          bgColor="gradient"
          textColor="white"
          useContactModal={true}
          modalTitle="Managed Security Consultation"
          modalDescription="Tell us about your managed security needs and how we can help protect your business."
        />
        
        <FaqSection />
      </main>
      
      <Footer />
    </div>
  );
};

export default ManagedCybersecurity;
